import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemText,
  Checkbox,
  OutlinedInput,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Chart as ChartJS,
  ScatterController,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import dragDataPlugin from 'chartjs-plugin-dragdata';
import { colorHandlerCode, colorByFundId, colorAdjustedProductType } from "../../../utils/colorEnumFormatter";
import { getHandlerCodeList, formatHandlerCode } from "../../../utils/namesFormater";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { Product, InvestmentPathDetail, AdjustedProductType } from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../../services/mimshakService";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { formatFundFieldLabel, getPercentileBucket, percentileBucketColors, percentileBucketLabels, allPercentileBuckets, getSubspecializationColor, getStockExposureRange, stockExposureColors, stockExposureRanges, getForeignExposureRange, foreignExposureColors, foreignExposureRanges, getCurrencyExposureRange, currencyExposureColors, currencyExposureRanges } from "../../../utils/fundsFormatter";
import { selectedFundOutlinePlugin, getHighlightedZeroLineGridConfig } from "../../../shared/UIParts/chartUtils";
import FundAccordionList from "./FundAccordionList";
import { PreferenceDistributionData } from "../../../interfaces/DTO/contactPreferences";


// Plugin להצגת שטח מוצל של טווח העדפות
const preferenceRangesPlugin = {
  id: 'preferenceRanges',

  afterDraw(chart: any) {
    const { ctx, chartArea, scales, options } = chart;
    const pluginOptions = options.plugins?.preferenceRanges;

    if (!pluginOptions || !pluginOptions.ranges) {
      return;
    }

    const { ranges } = pluginOptions;
    const xScale = scales.x;
    const yScale = scales.y;

    if (!xScale || !yScale) return;

    // ranges is now { field, axis, range } or null
    const { axis, range } = ranges;
    if (!axis || !range) return;

    const rangeToDisplay = range;
    const isXAxis = axis === 'x';
    const isYAxis = axis === 'y';

    ctx.save();

    // צייר רק שטח מוצל
    ctx.fillStyle = 'rgba(76, 175, 80, 0.1)'; // ירוק בהיר

    if (isXAxis) {
      // שטח מוצל אנכי
      const minPixel = xScale.getPixelForValue(rangeToDisplay.min);
      const maxPixel = xScale.getPixelForValue(rangeToDisplay.max);

      ctx.fillRect(
        minPixel,
        chartArea.top,
        maxPixel - minPixel,
        chartArea.bottom - chartArea.top
      );
    } else if (isYAxis) {
      // שטח מוצל אופקי
      const minPixel = yScale.getPixelForValue(rangeToDisplay.min);
      const maxPixel = yScale.getPixelForValue(rangeToDisplay.max);

      ctx.fillRect(
        chartArea.left,
        maxPixel,
        chartArea.right - chartArea.left,
        minPixel - maxPixel
      );
    }

    ctx.restore();
  },
};

ChartJS.register(ScatterController, LinearScale, PointElement, Tooltip, Legend, selectedFundOutlinePlugin, dragDataPlugin, preferenceRangesPlugin);

// Maps AdjustedProductType (or fallback ProductType) values to a merge group key.
// ACTIVE_MANAGERS_INSURANCE (111), INACTIVE_MANAGERS_INSURANCE (112), and LIFE_INSURANCE_SAVINGS (1)
// are all grouped together as "managers_insurance".
// CAPITAL_PROVIDENT_FUND (131), PENSION_PROVIDENT_FUND (132), COMBINED_PROVIDENT_FUND (133),
// and PROVIDENT_FUND (3) are all grouped as "provident_fund".
// Everything else gets its own group: "type_<value>".
const getProductGroup = (adjustedProductType?: number | null, productType?: number | null): string => {
  const type = adjustedProductType ?? productType;
  if (type === null || type === undefined) return 'type_unknown';
  // Managers insurance group
  if (
    type === AdjustedProductType.LIFE_INSURANCE_SAVINGS ||
    type === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
    type === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE
  ) return 'managers_insurance';
  // Provident fund group
  if (
    type === AdjustedProductType.PROVIDENT_FUND ||
    type === AdjustedProductType.CAPITAL_PROVIDENT_FUND ||
    type === AdjustedProductType.PENSION_PROVIDENT_FUND ||
    type === AdjustedProductType.COMBINED_PROVIDENT_FUND
  ) return 'provident_fund';
  return `type_${type}`;
};

interface HighlightedFundAmount {
  fund_id: number;
  amount: number;
  product_group: string;
  liquidity_year: number;
}

interface ScatterFundAmountChartProps {
  products: Product[];
  fundData?: FundDataBasic[];
  highlightedFundAmounts?: HighlightedFundAmount[];
  selectedFundIds?: number[];
  onSelectedFundsChange?: (fundIds: number[]) => void;
  yieldField?:
    | "avg_annual_yield_trailing_3yrs"
    | "avg_annual_yield_trailing_5yrs"
    | "cumulative_6m_yield"
    | "cumulative_12m_yield"
    | "year_to_date_yield"
    | "yield_trailing_3_yrs"
    | "yield_trailing_5_yrs";
  onYieldFieldChange?: (field: string) => void;
  initialYieldField?:
    | "avg_annual_yield_trailing_3yrs"
    | "avg_annual_yield_trailing_5yrs"
    | "cumulative_6m_yield"
    | "cumulative_12m_yield"
    | "year_to_date_yield"
    | "yield_trailing_3_yrs"
    | "yield_trailing_5_yrs";
  yAxisRange?: { min: number; max: number };
  initialColorMode?: 'default' | 'subspecialization' | 'managing_corporation' | 'fund_classification' | 'stock_exposure' | 'foreign_exposure' | 'currency_exposure' | 'percentile_6m' | 'percentile_12m';
  colorMode?: 'default' | 'subspecialization' | 'managing_corporation' | 'fund_classification' | 'stock_exposure' | 'foreign_exposure' | 'currency_exposure' | 'percentile_6m' | 'percentile_12m';
  onColorModeChange?: (mode: 'default' | 'subspecialization' | 'managing_corporation' | 'fund_classification' | 'stock_exposure' | 'foreign_exposure' | 'currency_exposure' | 'percentile_6m' | 'percentile_12m') => void;
  initialSelectedManagingCorporations?: string[];
  autoSelectHighlighted?: boolean;
  title?: string;
  initialXAxisField?: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year';
  xAxisField?: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year';
  onXAxisFieldChange?: (field: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year') => void;
  initialYAxisMode?: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation';
  initialDragEnabled?: boolean;
  yAxisMode?: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation';
  onYAxisModeChange?: (mode: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation') => void;
  dragEnabled?: boolean;
  onDragEnabledChange?: (enabled: boolean) => void;
  onPathDrag?: (dragData: {
    policy_number: string;
    product_type: number | null;
    path_code: number;
    old_x_value: number;
    new_x_value: number;
    old_y_value: number;
    new_y_value: number;
    x_field: 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year';
    y_field: 'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation';
  }) => void;
  dragChanges?: Array<{
    policy_number: string;
    path_code: number;
    new_x_value: number;
    new_y_value: number;
    x_field: string;
    y_field: string;
  }>;
  preferenceData?: PreferenceDistributionData;
  onPreferenceChange?: (newData: PreferenceDistributionData) => void;
}

// פונקציה עזר להוספת שקיפות לצבע
const addTransparency = (color: string, alpha: number = 0.7): string => {
  // אם הצבע כבר בפורמט rgba, החלף את האלפא
  if (color.startsWith('rgba(')) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${alpha})`);
  }
  
  // אם הצבע בפורמט rgb, המר ל-rgba
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
  }
  
  // אם הצבע בפורמט hex, המר ל-rgba
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  
  // אם זה צבע מוכר (כמו "orange"), השתמש בטבלת המרה בסיסית
  const namedColors: { [key: string]: string } = {
    'orange': 'rgba(255, 165, 0, ' + alpha + ')',
    'red': 'rgba(255, 0, 0, ' + alpha + ')',
    'blue': 'rgba(0, 0, 255, ' + alpha + ')',
    'green': 'rgba(0, 128, 0, ' + alpha + ')',
    'yellow': 'rgba(255, 255, 0, ' + alpha + ')',
    'purple': 'rgba(128, 0, 128, ' + alpha + ')',
    'cyan': 'rgba(0, 255, 255, ' + alpha + ')',
    'magenta': 'rgba(255, 0, 255, ' + alpha + ')',
  };
  
  return namedColors[color.toLowerCase()] || `rgba(128, 128, 128, ${alpha})`;
};

// פונקציה לקבלת צבעי חשיפה לפי מצב
const getExposureColorsWithTransparency = (colorMode: string): Record<string, string> => {
  if (colorMode === 'stock_exposure') {
    return Object.fromEntries(
      Object.entries(stockExposureColors).map(([key, color]) => [key, addTransparency(color, 0.75)])
    );
  } else if (colorMode === 'foreign_exposure') {
    return Object.fromEntries(
      Object.entries(foreignExposureColors).map(([key, color]) => [key, addTransparency(color, 0.75)])
    );
  } else if (colorMode === 'currency_exposure') {
    return Object.fromEntries(
      Object.entries(currencyExposureColors).map(([key, color]) => [key, addTransparency(color, 0.75)])
    );
  }
  return {};
};

// פונקציה לקבלת פונקציית הטווח לפי מצב
const getExposureRangeFunction = (colorMode: string) => {
  if (colorMode === 'stock_exposure') return getStockExposureRange;
  if (colorMode === 'foreign_exposure') return getForeignExposureRange;
  if (colorMode === 'currency_exposure') return getCurrencyExposureRange;
  return getStockExposureRange; // ברירת מחדל
};

// פונקציה לקבלת רשימת הטווחים לפי מצב
const getExposureRangesList = (colorMode: string): string[] => {
  if (colorMode === 'stock_exposure') return stockExposureRanges;
  if (colorMode === 'foreign_exposure') return foreignExposureRanges;
  if (colorMode === 'currency_exposure') return currencyExposureRanges;
  return stockExposureRanges; // ברירת מחדל
};

// פונקציה להמרת indices (0-9) לטווח אחוזים
const convertIndicesToRange = (indices: number[]): { min: number; max: number } | null => {
  if (!indices || indices.length === 0) {
    return null;
  }

  const minIndex = Math.min(...indices);
  const maxIndex = Math.max(...indices);

  // כל אינדקס מייצג 10% (0=0-10%, 1=10-20%, וכו')
  const min = minIndex * 10;
  const max = (maxIndex + 1) * 10;

  return { min, max };
};

const ScatterFundAmountChart: React.FC<ScatterFundAmountChartProps> = ({
  products,
  fundData = [],
  highlightedFundAmounts = [],
  selectedFundIds,
  onSelectedFundsChange,
  yieldField,
  onYieldFieldChange,
  initialYieldField = "cumulative_12m_yield",
  yAxisRange,
  initialColorMode = 'default',
  initialSelectedManagingCorporations = [],
  autoSelectHighlighted = false,
  title = "תרשים פיזור – תשואה מול חשיפה (גודל יחסי לסכום)",
  initialXAxisField = 'stock_market_percent',
  xAxisField: externalXAxisField,
  onXAxisFieldChange,
  initialYAxisMode = 'yield',
  initialDragEnabled = false,
  yAxisMode: externalYAxisMode,
  onYAxisModeChange,
  colorMode: externalColorMode,
  onColorModeChange,
  dragEnabled: externalDragEnabled,
  onDragEnabledChange,
  onPathDrag,
  dragChanges = [],
  preferenceData,
  onPreferenceChange,
}) => {
  // State פנימי כ-fallback אם לא מעבירים props
  const [internalSelectedFundIds, setInternalSelectedFundIds] = useState<number[]>(
    autoSelectHighlighted ? highlightedFundAmounts.map(h => h.fund_id) : []
  );
  const [internalYieldField, setInternalYieldField] = useState<
    | "avg_annual_yield_trailing_3yrs"
    | "avg_annual_yield_trailing_5yrs"
    | "cumulative_6m_yield"
    | "cumulative_12m_yield"
    | "year_to_date_yield"
    | "yield_trailing_3_yrs"
    | "yield_trailing_5_yrs"
  >(initialYieldField);
  
  const [internalXAxisField, setInternalXAxisField] = useState<'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year'>(initialXAxisField);
  const [internalYAxisMode, setInternalYAxisMode] = useState<'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation'>(initialYAxisMode);

  // Use external xAxisField if provided, otherwise use internal state
  const xAxisField = externalXAxisField ?? internalXAxisField;
  const actualSetXAxisField = (field: typeof xAxisField) => {
    if (onXAxisFieldChange) {
      onXAxisFieldChange(field);
    } else {
      setInternalXAxisField(field);
    }
  };
  const [selectedExposureRanges, setSelectedExposureRanges] = useState<string[]>([]);

  // Use external yAxisMode if provided, otherwise use internal state
  const yAxisMode = externalYAxisMode ?? internalYAxisMode;
  const actualSetYAxisMode = (mode: typeof yAxisMode) => {
    if (onYAxisModeChange) {
      onYAxisModeChange(mode);
    } else {
      setInternalYAxisMode(mode);
    }
  };

  // State לגודל ועיצוב
  const [pointSize, setPointSize] = useState<'normal' | 'small' | 'highlight_focus'>('normal');
  const [internalColorMode, setInternalColorMode] = useState<'default' | 'subspecialization' | 'managing_corporation' | 'fund_classification' | 'stock_exposure' | 'foreign_exposure' | 'currency_exposure' | 'percentile_6m' | 'percentile_12m'>(initialColorMode);
  const [selectedSubSpecializations, setSelectedSubSpecializations] = useState<string[]>([]);

  // Use external colorMode if provided, otherwise use internal state
  const colorMode = externalColorMode ?? internalColorMode;
  const actualSetColorMode = (mode: typeof colorMode) => {
    if (onColorModeChange) {
      onColorModeChange(mode);
    } else {
      setInternalColorMode(mode);
    }
  };
  const [selectedManagingCorporations, setSelectedManagingCorporations] = useState<string[]>(initialSelectedManagingCorporations);
  const [selectedPercentileBuckets, setSelectedPercentileBuckets] = useState<string[]>([]);

  // Ref לתרשים כדי לשלוט באנימציה
  const chartRef = useRef<any>(null);

  // State לבחירת fundId להדגשה
  const [selectedScatterFundId, setSelectedScatterFundId] = useState<number | null>(null);

  // State להפעלת/כיבוי מצב Drag
  const [internalIsDragEnabled, setInternalIsDragEnabled] = useState<boolean>(initialDragEnabled);

  // Use external dragEnabled if provided, otherwise use internal state
  const isDragEnabled = externalDragEnabled ?? internalIsDragEnabled;
  const actualSetIsDragEnabled = (enabled: boolean) => {
    if (onDragEnabledChange) {
      onDragEnabledChange(enabled);
    } else {
      setInternalIsDragEnabled(enabled);
    }
  };

  // השתמש ב-props אם קיימים, אחרת ב-state פנימי
  const actualSelectedFundIds = selectedFundIds ?? internalSelectedFundIds;
  const actualYieldField = yieldField ?? internalYieldField;
  const actualOnSelectedFundsChange = onSelectedFundsChange ?? setInternalSelectedFundIds;
  const actualOnYieldFieldChange = onYieldFieldChange ?? setInternalYieldField;

  // חישוב טווחי העדפות לציר הרלוונטי
  const preferenceRanges = useMemo(() => {
    if (!preferenceData) return null;

    // Determine which field to show based on current axes
    let field: 'stock_market_percent' | 'foreign_currency_exposure_percent' | 'foreign_exposure_percent' | null = null;
    let axis: 'x' | 'y' | null = null;
    let range: { min: number; max: number } | null = null;

    // Priority: Y axis takes precedence over X axis
    if (yAxisMode === 'stock_market_percent' && preferenceData.stockExposure?.length > 0) {
      field = 'stock_market_percent';
      axis = 'y';
      range = convertIndicesToRange(preferenceData.stockExposure);
    } else if (yAxisMode === 'foreign_currency_exposure_percent' && preferenceData.currencyExposure?.length > 0) {
      field = 'foreign_currency_exposure_percent';
      axis = 'y';
      range = convertIndicesToRange(preferenceData.currencyExposure);
    } else if (yAxisMode === 'foreign_exposure_percent' && preferenceData.foreignExposure?.length > 0) {
      field = 'foreign_exposure_percent';
      axis = 'y';
      range = convertIndicesToRange(preferenceData.foreignExposure);
    } else if (xAxisField === 'stock_market_percent' && preferenceData.stockExposure?.length > 0) {
      field = 'stock_market_percent';
      axis = 'x';
      range = convertIndicesToRange(preferenceData.stockExposure);
    } else if (xAxisField === 'foreign_currency_exposure_percent' && preferenceData.currencyExposure?.length > 0) {
      field = 'foreign_currency_exposure_percent';
      axis = 'x';
      range = convertIndicesToRange(preferenceData.currencyExposure);
    } else if (xAxisField === 'foreign_exposure_percent' && preferenceData.foreignExposure?.length > 0) {
      field = 'foreign_exposure_percent';
      axis = 'x';
      range = convertIndicesToRange(preferenceData.foreignExposure);
    }

    if (!field || !axis || !range) return null;

    return { field, axis, range };
  }, [preferenceData, xAxisField, yAxisMode]);

  // פונקציה לקבלת כותרת ציר X
  const getXAxisTitle = () => {
    switch (xAxisField) {
      case "stock_market_percent": return "% חשיפה למניות";
      case "foreign_exposure_percent": return "% חשיפה לחו\"ל";
      case "foreign_currency_exposure_percent": return "% חשיפה למט\"ח";
      case "liquidity_year": return "שנת נזילות";
      default: return "% חשיפה";
    }
  };

  // פונקציה לקבלת כותרת ציר Y
  const getYAxisTitle = () => {
    if (yAxisMode === 'yield') {
      return formatFundFieldLabel(actualYieldField).label;
    }
    switch (yAxisMode) {
      case "stock_market_percent": return "% חשיפה למניות";
      case "foreign_exposure_percent": return "% חשיפה לחו\"ל";
      case "foreign_currency_exposure_percent": return "% חשיפה למט\"ח";
      case "liquidity_year": return "שנת נזילות";
      case "standard_deviation": return "תנודתיות 5Y";
      default: return "";
    }
  };

  // חילוץ מידע על השקעות מהמוצרים
  // Helper: extract the effective liquidity year from a policy
  const getPolicyLiquidityYear = (policy: { liquidity_summaries?: Array<{ liquidity_date?: string }> }): number => {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 60;
    const summaries = policy.liquidity_summaries || [];
    const summaryWithDate = summaries.find(s => s.liquidity_date);
    if (summaryWithDate?.liquidity_date) {
      const year = new Date(summaryWithDate.liquidity_date).getFullYear();
      if (year < currentYear) return currentYear;
      if (year > maxYear) return maxYear;
      return year;
    }
    return maxYear;
  };

  const investmentData = useMemo(() => {
    const data: HighlightedFundAmount[] = [];
    // Only split circles by liquidity year when the X axis IS liquidity_year.
    // Otherwise treat all liquidity years as the same (merge by fund_id + group only).
    const splitByLiquidityYear = xAxisField === 'liquidity_year';
    const currentYear = new Date().getFullYear();
    const defaultLiquidityYear = currentYear + 60;

    products.forEach(product => {
      const group = getProductGroup(
        product.details?.adjusted_product_type,
        product.details?.product_type
      );

      product.policies_or_accounts?.forEach(policy => {
        const liquidityYear = splitByLiquidityYear ? getPolicyLiquidityYear(policy) : defaultLiquidityYear;
        const mergedPaths: InvestmentPathDetail[] = MimshakService.mergeDuplicateInvestmentPaths(
          policy.investment_paths || []
        );

        mergedPaths.forEach(path => {
          if (path.path_code !== undefined && path.path_code !== null && path.accumulated_amount && path.accumulated_amount > 0) {
            const existingIndex = data.findIndex(
              item => item.fund_id === path.path_code && item.product_group === group && item.liquidity_year === liquidityYear
            );
            if (existingIndex >= 0) {
              data[existingIndex].amount += path.accumulated_amount;
            } else {
              data.push({
                fund_id: path.path_code,
                amount: path.accumulated_amount,
                product_group: group,
                liquidity_year: liquidityYear,
              });
            }
          }
        });
      });
    });

    return data;
  }, [products, xAxisField]);

  // Helper: build the map key matching how investmentData splits circles.
  // When X axis is liquidity_year, each distinct liquidity year gets its own circle.
  // Otherwise, all policies in the same (path_code, group) share one circle.
  const getMapKey = (pathCode: number, group: string, policy: { liquidity_summaries?: Array<{ liquidity_date?: string }> }) => {
    const currentYear = new Date().getFullYear();
    const liquidityYear = xAxisField === 'liquidity_year'
      ? getPolicyLiquidityYear(policy)
      : currentYear + 60;
    return `${pathCode}_${group}_${liquidityYear}`;
  };

  // מיפוי "pathCode_group[_liquidityYear]" ל-adjusted_product_type
  const fundToAdjustedProductTypeMap = useMemo(() => {
    const map: { [key: string]: number } = {};

    products.forEach(product => {
      const adjustedProductType = product.details?.adjusted_product_type;
      if (adjustedProductType !== undefined && adjustedProductType !== null) {
        const group = getProductGroup(adjustedProductType, product.details?.product_type);
        product.policies_or_accounts?.forEach(policy => {
          const mergedPaths: InvestmentPathDetail[] = MimshakService.mergeDuplicateInvestmentPaths(
            policy.investment_paths || []
          );

          mergedPaths.forEach(path => {
            if (path.path_code !== undefined && path.path_code !== null) {
              map[getMapKey(path.path_code, group, policy)] = adjustedProductType;
            }
          });
        });
      }
    });

    return map;
  }, [products, xAxisField]);

  // מיפוי "pathCode_group[_liquidityYear]" לשם לקוח
  const fundToClientNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};

    products.forEach(product => {
      const clientName = product.client?.client_name;
      if (clientName) {
        const group = getProductGroup(product.details?.adjusted_product_type, product.details?.product_type);
        product.policies_or_accounts?.forEach(policy => {
          const mergedPaths: InvestmentPathDetail[] = MimshakService.mergeDuplicateInvestmentPaths(
            policy.investment_paths || []
          );

          mergedPaths.forEach(path => {
            if (path.path_code !== undefined && path.path_code !== null) {
              map[getMapKey(path.path_code, group, policy)] = clientName;
            }
          });
        });
      }
    });

    return map;
  }, [products, xAxisField]);

  // מיפוי "pathCode_group[_liquidityYear]" לרשימת פוליסות וסוג מוצר (כל מוצר שמרכיב את הקבוצה)
  const fundToPolicyMap = useMemo(() => {
    const map: { [key: string]: Array<{ policy_number: string; product_type: number | null }> } = {};

    products.forEach(product => {
      const productType = product.details?.adjusted_product_type ?? product.details?.product_type ?? null;
      const group = getProductGroup(product.details?.adjusted_product_type, product.details?.product_type);

      product.policies_or_accounts?.forEach(policy => {
        const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths || []);

        mergedPaths.forEach(path => {
          if (path.path_code !== undefined && path.path_code !== null) {
            const key = getMapKey(path.path_code, group, policy);
            if (!map[key]) map[key] = [];
            const entry = { policy_number: policy.policy_number || '', product_type: productType };
            // Avoid duplicate policy entries
            if (!map[key].some(e => e.policy_number === entry.policy_number)) {
              map[key].push(entry);
            }
          }
        });
      });
    });

    return map;
  }, [products, xAxisField]);

  // מיפוי "pathCode_group[_liquidityYear]" לשנת נזילות
  const fundToLiquidityYearMap = useMemo(() => {
    const map: { [key: string]: number } = {};
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 60;

    products.forEach(product => {
      const group = getProductGroup(product.details?.adjusted_product_type, product.details?.product_type);
      product.policies_or_accounts?.forEach(policy => {
        const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths || []);
        const liquidityYear = getPolicyLiquidityYear(policy);

        mergedPaths.forEach(path => {
          if (path.path_code !== undefined && path.path_code !== null) {
            const mapKey = getMapKey(path.path_code, group, policy);
            // When splitting by liquidity year, the year is already encoded in the key.
            // When not splitting, take the earliest year across all policies.
            if (xAxisField === 'liquidity_year') {
              map[mapKey] = liquidityYear;
            } else if (map[mapKey] === undefined || liquidityYear < map[mapKey]) {
              map[mapKey] = liquidityYear;
            }
          }
        });
      });
    });

    return map;
  }, [products, xAxisField]);

  // מיזוג הנתונים עם highlighted amounts
  const allHighlightedAmounts = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 60;
    // highlightedFundAmounts from props don't carry product_group/liquidity_year, assign defaults
    const combined: HighlightedFundAmount[] = highlightedFundAmounts.map(h => ({
      ...h,
      product_group: (h as any).product_group ?? 'type_unknown',
      liquidity_year: (h as any).liquidity_year ?? maxYear,
    }));

    // Add investment data; merge only when fund_id, product_group AND liquidity_year all match
    investmentData.forEach(investment => {
      const exists = combined.find(
        h => h.fund_id === investment.fund_id && h.product_group === investment.product_group && h.liquidity_year === investment.liquidity_year
      );
      if (!exists) {
        combined.push(investment);
      }
    });

    return combined;
  }, [highlightedFundAmounts, investmentData]);

  // מפה של fundId -> amount לשימוש ב-FundAccordionList (סכום כולל מכל הקבוצות)
  const fundAmountMap = useMemo(() => {
    const map = new Map<number, number>();
    allHighlightedAmounts.forEach(item => {
      map.set(item.fund_id, (map.get(item.fund_id) ?? 0) + item.amount);
    });
    return map;
  }, [allHighlightedAmounts]);

  const fundClassificationColors: Record<string, string> = {
    "פנסיה מקיפה": addTransparency("#AB47BC", 0.75),
    "פנסיה משלימה": addTransparency("#42A5F5", 0.75),
    "כללי": addTransparency("#66BB6A", 0.75),
    "-": addTransparency("#BDBDBD", 0.6),
  };

  // מציאת כל ערכי sub_specialization הייחודיים
  const allSubSpecializations = useMemo(() => {
    const uniqueValues = [...new Set(fundData.map(f => f.sub_specialization || '-'))];
    return uniqueValues.sort();
  }, [fundData]);

  // מציאת כל יצרני הקרנות הכלליים
  const generalManagingCorporations = useMemo(() => {
    const handlerList = getHandlerCodeList();
    return handlerList.filter(handler => handler.isGeneral);
  }, []);

  // מיפוי צבעים לsub_specialization
  const subspecializationColorMap = useMemo(() => {
    const colorMap: { [key: string]: string } = {};
    allSubSpecializations.forEach((spec) => {
      colorMap[spec] = getSubspecializationColor(spec);
    });
    return colorMap;
  }, [allSubSpecializations]);

  // חישוב טווח סכומים לגודל עיגולים
  const amountRange = useMemo(() => {
    const amounts = allHighlightedAmounts.map(h => h.amount);
    const min = Math.min(...amounts, 5);
    const max = Math.max(...amounts, 2000000);
    return { min, max };
  }, [allHighlightedAmounts]);

  // פונקציה לחישוב גודל עיגול לפי סכום
  const calculatePointSize = (amount: number, baseSize: number) => {
    const minSize = baseSize * 1.1;
    const maxSize = baseSize * 4.5;

    const sqrtMin = Math.sqrt(Math.max(amountRange.min, 1));
    const sqrtMax = Math.sqrt(amountRange.max);
    const sqrtAmount = Math.sqrt(Math.max(amount, 1));

    const normalizedValue = (sqrtAmount - sqrtMin) / (sqrtMax - sqrtMin);
    return minSize + (maxSize - minSize) * normalizedValue;
  };

  const chartData = useMemo(() => {
    // Build preference handle points first
    const preferenceHandles: any[] = [];

    if (preferenceRanges) {
      const { field, range, axis } = preferenceRanges;

      // Create min handle
      const minHandle: any = {
        label: `העדפה - ${field} - מינימום`,
        isPreferenceHandle: true,
        preferenceType: 'min',
        preferenceField: field,
        preferenceAxis: axis,
        backgroundColor: 'rgba(100, 230, 245, 0.85)',
        borderColor: 'white',
        borderWidth: 2,
        isImportant: true,
        amount: 0,
      };

      // Create max handle
      const maxHandle: any = {
        label: `העדפה - ${field} - מקסימום`,
        isPreferenceHandle: true,
        preferenceType: 'max',
        preferenceField: field,
        preferenceAxis: axis,
        backgroundColor: 'rgba(100, 230, 245, 0.85)',
        borderColor: 'white',
        borderWidth: 2,
        isImportant: true,
        amount: 0,
      };

      // Set x/y values based on axis
      if (axis === 'y') {
        // Y axis - handles are horizontal lines
        minHandle.x = 50; // Fixed X value in the middle
        minHandle.y = range.min;
        maxHandle.x = 50;
        maxHandle.y = range.max;
      } else {
        // X axis - handles are vertical lines
        minHandle.x = range.min;
        minHandle.y = 50; // Fixed Y value in the middle
        maxHandle.x = range.max;
        maxHandle.y = 50;
      }

      preferenceHandles.push(minHandle, maxHandle);
    }

    // Build one scatter point per (fund × product_group × liquidity_year) combination that has an amount.
    // allHighlightedAmounts already has one entry per (fund_id, product_group, liquidity_year) with the
    // merged amount, so we iterate over those and look up the fund from fundData.
    const data = allHighlightedAmounts
      .filter(h => h.amount > 0)
      .flatMap((highlightedItem) => {
        const fund = fundData.find(f => f.fund_id === highlightedItem.fund_id);
        if (!fund) return [];

        const group = highlightedItem.product_group;
        const mapKey = `${fund.fund_id}_${group}_${highlightedItem.liquidity_year}`;

        // בדיקת ערך ציר Y
        let hasYValue: boolean;
        if (yAxisMode === 'yield') {
          hasYValue = fund[actualYieldField] !== undefined && fund[actualYieldField] !== null;
        } else if (yAxisMode === 'liquidity_year') {
          hasYValue = true;
        } else if (yAxisMode === 'standard_deviation') {
          hasYValue = fund.standard_deviation !== undefined && fund.standard_deviation !== null;
        } else {
          hasYValue = fund[yAxisMode] !== undefined && fund[yAxisMode] !== null;
        }

        // בדיקת ערך ציר X
        const hasXValue = xAxisField === 'liquidity_year' || (fund[xAxisField] !== undefined && fund[xAxisField] !== null);

        if (!hasYValue || !hasXValue) return [];

        const isSelected = actualSelectedFundIds.includes(fund.fund_id);
        const isHighlighted = true; // allHighlightedAmounts only contains highlighted items
        const isImportant = isSelected || isHighlighted;
        const amount = highlightedItem.amount;

        const policyInfoList = fundToPolicyMap[mapKey] ?? [];
        // For drag change lookup, check any of the policy numbers in the group
        const policyNumbers = policyInfoList.map(p => p.policy_number);

        // מצא את כל השינויים עבור המסלול הזה (מכל הפוליסות שבקבוצה)
        const allDragChanges = dragChanges.filter(
          dc => dc.path_code === fund.fund_id && policyNumbers.includes(dc.policy_number)
        );

        // חישוב ערך ציר X המקורי
        let originalXValue: number;
        if (xAxisField === 'liquidity_year') {
          originalXValue = fundToLiquidityYearMap[mapKey] || (new Date().getFullYear() + 60);
        } else {
          originalXValue = fund[xAxisField] as number;
        }

        // חישוב ערך ציר Y המקורי
        let originalYValue: number;
        if (yAxisMode === 'yield') {
          originalYValue = fund[actualYieldField] as number;
        } else if (yAxisMode === 'liquidity_year') {
          originalYValue = fundToLiquidityYearMap[mapKey] || (new Date().getFullYear() + 60);
        } else if (yAxisMode === 'standard_deviation') {
          originalYValue = fund.standard_deviation as number;
        } else {
          originalYValue = fund[yAxisMode] as number;
        }

        // חישוב ערך ציר X - בדוק אם יש שינוי גרירה
        let xValue: number;
        const xDragChange = allDragChanges.find(dc => dc.x_field === xAxisField);
        if (xDragChange) {
          xValue = xDragChange.new_x_value;
        } else {
          xValue = originalXValue;
        }

        // חישוב ערך ציר Y - בדוק אם יש שינוי גרירה
        let yValue: number;
        const yDragChange = allDragChanges.find(dc => dc.y_field === yAxisMode);
        if (yDragChange) {
          yValue = yDragChange.new_y_value;
        } else {
          yValue = originalYValue;
        }

        let backgroundColor: string;

        if (colorMode === 'subspecialization') {
          const subspecialization = fund.sub_specialization || '-';
          const shouldShow = selectedSubSpecializations.length === 0 ||
                           selectedSubSpecializations.includes(subspecialization);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.4)' : 'rgba(200,200,200,0.2)';
          } else {
            backgroundColor = shouldShow ? subspecializationColorMap[subspecialization] : 'rgba(200,200,200,0.2)';
          }
        } else if (colorMode === 'managing_corporation') {
          const managingCorp = fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : '-';
          const shouldShow = selectedManagingCorporations.length === 0 ||
                           selectedManagingCorporations.includes(managingCorp);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.4)' : 'rgba(200,200,200,0.2)';
          } else {
            backgroundColor = shouldShow && fund.managing_corporation_legal_id
              ? addTransparency(colorHandlerCode(fund.managing_corporation_legal_id), 0.7)
              : shouldShow
              ? 'rgba(128,128,128,0.4)'
              : 'rgba(200,200,200,0.2)';
          }
        } else if (colorMode === 'stock_exposure' || colorMode === 'foreign_exposure' || colorMode === 'currency_exposure') {
          const exposureRangeFunc = getExposureRangeFunction(colorMode);
          const exposureColors = getExposureColorsWithTransparency(colorMode);

          let exposure = 0;
          if (colorMode === 'stock_exposure') {
            exposure = fund.stock_market_percent || 0;
          } else if (colorMode === 'foreign_exposure') {
            exposure = fund.foreign_exposure_percent || 0;
          } else if (colorMode === 'currency_exposure') {
            exposure = fund.foreign_currency_exposure_percent || 0;
          }

          const range = exposureRangeFunc(exposure);
          const shouldShow = selectedExposureRanges.length === 0 || selectedExposureRanges.includes(range);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.4)' : 'rgba(200,200,200,0.2)';
          } else {
            backgroundColor = shouldShow ? exposureColors[range] : 'rgba(200,200,200,0.2)';
          }
        } else if (colorMode === 'fund_classification') {
          const classification = fund.fund_classification || "-";

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = 'rgba(240, 240, 240, 0.4)';
          } else {
            backgroundColor = fundClassificationColors[classification] || addTransparency("#BDBDBD", 0.6);
          }
        } else if (colorMode === 'percentile_6m') {
          const percentileValue = fund.percentile_6m_yield || 0;
          const percentileBucket = getPercentileBucket(percentileValue);
          const shouldShow = selectedPercentileBuckets.length === 0 || selectedPercentileBuckets.includes(percentileBucket);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.4)' : 'rgba(200,200,200,0.2)';
          } else {
            backgroundColor = shouldShow ? percentileBucketColors[percentileBucket] : 'rgba(200,200,200,0.2)';
          }
        } else if (colorMode === 'percentile_12m') {
          const percentileValue = fund.percentile_12m_yield || 0;
          const percentileBucket = getPercentileBucket(percentileValue);
          const shouldShow = selectedPercentileBuckets.length === 0 || selectedPercentileBuckets.includes(percentileBucket);

          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = shouldShow ? 'rgba(240, 240, 240, 0.4)' : 'rgba(200,200,200,0.2)';
          } else {
            backgroundColor = shouldShow ? percentileBucketColors[percentileBucket] : 'rgba(200,200,200,0.2)';
          }
        } else {
          // מצב default - צביעה לפי adjusted_product_type
          if (pointSize === 'highlight_focus' && !isImportant) {
            backgroundColor = 'rgba(240, 240, 240, 0.4)';
          } else {
            const adjustedProductType = fundToAdjustedProductTypeMap[mapKey];
            if (adjustedProductType !== undefined && adjustedProductType !== null) {
              backgroundColor = addTransparency(colorAdjustedProductType(adjustedProductType), 0.7);
            } else if (isSelected) {
              backgroundColor = addTransparency(colorByFundId(fund.fund_id), 0.8);
            } else if (isHighlighted) {
              backgroundColor = addTransparency("orange", 0.8);
            } else {
              backgroundColor = "rgba(75,192,192,0.6)";
            }
          }
        }

        // הגדרת border
        let borderColor: string;
        let borderWidth: number = 1;

        if (pointSize === 'highlight_focus' && !isImportant) {
          borderColor = 'rgba(180, 180, 180, 0.5)';
          borderWidth = 1;
        } else if (colorMode === 'managing_corporation') {
          const managingCorp = fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : '-';
          const isSelectedCorp = selectedManagingCorporations.length > 0 && selectedManagingCorporations.includes(managingCorp);

          if (isSelectedCorp && fund.managing_corporation_legal_id) {
            const baseColor = colorHandlerCode(fund.managing_corporation_legal_id);
            borderColor = addTransparency(baseColor, 0.9);
            borderWidth = 2;
          } else {
            borderColor = 'rgba(0,0,0,0.15)';
          }
        } else {
          borderColor = 'rgba(0,0,0,0.15)';
        }

        return [{
          x: xValue,
          y: yValue,
          originalX: originalXValue,
          originalY: originalYValue,
          label: fund.fund_name || "ללא שם",
          fund_id: fund.fund_id,
          subspecialization: fund.sub_specialization || '-',
          fund_classification: fund.fund_classification || "-",
          managing_corporation_legal_id: fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : undefined,
          adjusted_product_type: fundToAdjustedProductTypeMap[mapKey],
          client_name: fundToClientNameMap[mapKey],
          percentile_6m_yield: fund.percentile_6m_yield || 0,
          percentile_12m_yield: fund.percentile_12m_yield || 0,
          percentile_6m_bucket: getPercentileBucket(fund.percentile_6m_yield || 0),
          percentile_12m_bucket: getPercentileBucket(fund.percentile_12m_yield || 0),
          liquidity_year: fundToLiquidityYearMap[mapKey] || (new Date().getFullYear() + 60),
          yieldValue: fund[actualYieldField] as number,
          stock_market_percent: fund.stock_market_percent || 0,
          foreign_exposure_percent: fund.foreign_exposure_percent || 0,
          foreign_currency_exposure_percent: fund.foreign_currency_exposure_percent || 0,
          // Keep first policy_number for backwards-compat display; all_policy_infos used for drag
          policy_number: policyInfoList[0]?.policy_number || '',
          product_type: policyInfoList[0]?.product_type || null,
          all_policy_infos: policyInfoList,
          path_code: fund.fund_id,
          backgroundColor,
          borderColor,
          borderWidth,
          isImportant,
          amount,
          fundId: fund.fund_id,
          isSelected: isSelected,
        }];
      });

    // Add preference handles to the data
    const allData = [...data, ...preferenceHandles];

    // הגדרת גודל נקודות
    let pointRadius: number | ((ctx: any) => number);
    let pointHoverRadius: number | ((ctx: any) => number);

    if (pointSize === 'highlight_focus') {
      pointRadius = (ctx: any) => {
        if (ctx.raw.isPreferenceHandle) {
          return 8; // Preference handles always size 8
        }
        if (ctx.raw.isImportant && ctx.raw.amount > 0) {
          return calculatePointSize(ctx.raw.amount, 5);
        }
        return ctx.raw.isImportant ? 5 : 2;
      };
      pointHoverRadius = (ctx: any) => {
        if (ctx.raw.isPreferenceHandle) {
          return 10; // Larger on hover
        }
        if (ctx.raw.isImportant && ctx.raw.amount > 0) {
          return calculatePointSize(ctx.raw.amount, 5) + 1;
        }
        return ctx.raw.isImportant ? 6 : 3;
      };
    } else {
      const baseSize = pointSize === 'small' ? 3 : 5;
      pointRadius = (ctx: any) => {
        if (ctx.raw.isPreferenceHandle) {
          return 8; // Preference handles always size 8
        }
        if (ctx.raw.amount > 0) {
          return calculatePointSize(ctx.raw.amount, baseSize);
        }
        return baseSize;
      };
      pointHoverRadius = (ctx: any) => {
        if (ctx.raw.isPreferenceHandle) {
          return 10; // Larger on hover
        }
        if (ctx.raw.amount > 0) {
          return calculatePointSize(ctx.raw.amount, baseSize) + 1;
        }
        return baseSize + 1;
      };
    }

    return {
      datasets: [
        {
          label: "",
          data: allData,
          backgroundColor: (ctx: any) => ctx.raw.backgroundColor,
          borderColor: (ctx: any) => ctx.raw.borderColor,
          borderWidth: (ctx: any) => ctx.raw.borderWidth,
          pointRadius,
          pointHoverRadius,
        },
      ],
    };
  }, [fundData, actualYieldField, actualSelectedFundIds, allHighlightedAmounts, colorMode, selectedSubSpecializations, selectedManagingCorporations, subspecializationColorMap, pointSize, amountRange, fundToAdjustedProductTypeMap, fundToClientNameMap, xAxisField, selectedExposureRanges, selectedPercentileBuckets, fundToLiquidityYearMap, yAxisMode, fundToPolicyMap, dragChanges, preferenceRanges]);

  // Cursor management for preference handles
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !preferenceRanges || !isDragEnabled) return;

    const canvas = chart.canvas;
    if (!canvas) return;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const { chartArea, scales } = chart;
      if (!chartArea || !scales.x || !scales.y) return;

      // Get all data points
      const dataset = chartData.datasets[0];
      if (!dataset || !dataset.data) return;

      // Check if mouse is near any preference handle
      let nearPreferenceHandle = false;

      for (let i = 0; i < dataset.data.length; i++) {
        const point = dataset.data[i] as any;
        if (!point.isPreferenceHandle) continue;

        const meta = chart.getDatasetMeta(0);
        const element = meta.data[i];

        if (element) {
          const { x, y } = element.getCenterPoint();
          const distance = Math.sqrt(Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2));

          if (distance <= 12) { // Within hover threshold
            nearPreferenceHandle = true;
            // Set cursor based on axis
            if (point.preferenceAxis === 'y') {
              canvas.style.cursor = 'ns-resize';
            } else {
              canvas.style.cursor = 'ew-resize';
            }
            break;
          }
        }
      }

      if (!nearPreferenceHandle) {
        canvas.style.cursor = 'default';
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.style.cursor = 'default';
    };
  }, [chartRef, preferenceRanges, isDragEnabled, chartData]);

  const formatAmount = (amount: number) => {
    if (amount === 0) {
      return "0 ₪";
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₪`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K ₪`;
    }
    return `${amount.toFixed(0)} ₪`;
  };

    const chartOptions = useMemo(() => {
    // בודק אם קיימים ערכים שליליים בציר ה־Y
    const hasNegativeValues =
      chartData.datasets[0]?.data?.some((d: any) => d.y < 0) ?? false;

    // בדוק את ערכי X והמינימליים והמקסימליים בנתונים
    const xValues = chartData.datasets[0]?.data?.map((d: any) => d.x) ?? [];
    const minX = xValues.length > 0 ? Math.min(...xValues) : 0;
    const maxX = xValues.length > 0 ? Math.max(...xValues) : 100;

    // בדוק את ערכי Y המינימליים והמקסימליים בנתונים
    const yValues = chartData.datasets[0]?.data?.map((d: any) => d.y) ?? [];
    const minY = yValues.length > 0 ? Math.min(...yValues) : 0;
    const maxY = yValues.length > 0 ? Math.max(...yValues) : 100;

    // קבע את גבולות ציר X בהתאם לנתונים
    const getXAxisBounds = () => {
      if (xAxisField === 'liquidity_year') {
        return {
          min: new Date().getFullYear() - 1,
          max: new Date().getFullYear() + 61,
        };
      }

      let min = 0;
      let max = 100;

      // אם יש ערך קטן מ-2, הוסף מרווח של -5
      if (minX < 2) {
        min = -5;
      }
      // אם יש ערך גדול מ-98, הוסף מרווח עד 105
      if (maxX > 98) {
        max = 105;
      }

      return { min, max };
    };

    // קבע את גבולות ציר Y בהתאם לנתונים
    const getYAxisBounds = () => {
      // בדוק אם ציר Y הוא אחד משדות החשיפה באחוזים
      const isPercentageField = yAxisMode === 'stock_market_percent' ||
                                yAxisMode === 'foreign_exposure_percent' ||
                                yAxisMode === 'foreign_currency_exposure_percent';

      if (isPercentageField && !yAxisRange) {
        let min = 0;
        let max = 100;

        // אם יש ערך קטן מ-2, הוסף מרווח של -5
        if (minY < 2) {
          min = -5;
        }
        // אם יש ערך גדול מ-98, הוסף מרווח עד 105
        if (maxY > 98) {
          max = 105;
        }

        return { min, max };
      }

      return null; // השתמש בהגדרות ברירת המחדל
    };

    const xAxisBounds = getXAxisBounds();
    const yAxisBounds = getYAxisBounds();

    return {
      selectedFundId: selectedScatterFundId, // העבר את ה-fundId הנבחר לפלאגין
      maintainAspectRatio: false,
      responsive: true,
      animation: {
        duration: 750, // אנימציה רגילה לשינויים בשדות תצוגה
      },
      transitions: {
        active: {
          animation: {
            duration: 0, // ביטול אנימציה בעת hover
          }
        }
      },
      scales: {
        x: {
          type: "linear" as const,
          title: {
            display: true,
            text: getXAxisTitle(),
            font: {
              size: 16,
              weight: 'bold' as const,
            },
          },
          min: xAxisBounds.min,
          max: xAxisBounds.max,
        },
        y: {
          type: "linear" as const,
          title: {
            display: true,
            text: getYAxisTitle(),
            font: {
              size: 16,
              weight: 'bold' as const,
            },
          },
          beginAtZero: !hasNegativeValues && !yAxisRange && yAxisMode !== 'liquidity_year' && !yAxisBounds,
          ...(yAxisRange && {
            min: yAxisRange.min,
            max: yAxisRange.max,
          }),
          ...(yAxisMode === 'liquidity_year' && !yAxisRange && {
            min: new Date().getFullYear() - 1,
            max: new Date().getFullYear() + 61,
          }),
          ...(yAxisBounds && {
            min: yAxisBounds.min,
            max: yAxisBounds.max,
          }),
          grid: getHighlightedZeroLineGridConfig(), // תמיד הדגש את קו Y=0
        },
      },
      plugins: {
        dragData: {
          round: 2,
          showTooltip: true,
          dragX: function(_e: any, datasetIndex: number, index: number, _value: any) {
            if (!isDragEnabled) return false;

            const pointData = chartData.datasets[datasetIndex]?.data[index];
            if (!pointData) return false;

            // For preference handles, allow X drag only if on X axis
            if ((pointData as any).isPreferenceHandle) {
              return (pointData as any).preferenceAxis === 'x';
            }

            // For regular points, allow X drag if not liquidity_year
            return xAxisField !== 'liquidity_year';
          } as any,
          dragY: function(_e: any, datasetIndex: number, index: number, _value: any) {
            if (!isDragEnabled) return false;

            const pointData = chartData.datasets[datasetIndex]?.data[index];
            if (!pointData) return false;

            // For preference handles, allow Y drag only if on Y axis
            if ((pointData as any).isPreferenceHandle) {
              return (pointData as any).preferenceAxis === 'y';
            }

            // For regular points, always allow Y drag
            return true;
          } as any,
          onDragStart: function(_e: any, _datasetIndex: number, _index: number, _value: any) {
            // מנע drag אם המצב לא מופעל
            return isDragEnabled;
          },
          onDrag: function(e: any, _datasetIndex: number, _index: number, _value: any) {
            // עדכון בזמן אמת של הערך
            e.target.style.cursor = 'grabbing';
          },
          onDragEnd: function(e: any, datasetIndex: number, index: number, value: any) {
            e.target.style.cursor = 'default';

            // קבל את הנתונים של הנקודה שנגררה
            const pointData = chartData.datasets[datasetIndex]?.data[index];

            if (!pointData) return;

            // Check if this is a preference handle
            if ((pointData as any).isPreferenceHandle) {
              if (onPreferenceChange && preferenceData) {
                const { preferenceType, preferenceField, preferenceAxis } = pointData as any;
                const newValue = preferenceAxis === 'y' ? value.y : value.x;

                // Convert percentage back to indices
                const newIndex = Math.floor(newValue / 10);
                const clampedIndex = Math.max(0, Math.min(9, newIndex));

                // Get the current indices for the field
                let currentIndices: number[] = [];
                if (preferenceField === 'stock_market_percent') {
                  currentIndices = [...(preferenceData.stockExposure || [])];
                } else if (preferenceField === 'foreign_currency_exposure_percent') {
                  currentIndices = [...(preferenceData.currencyExposure || [])];
                } else if (preferenceField === 'foreign_exposure_percent') {
                  currentIndices = [...(preferenceData.foreignExposure || [])];
                }

                if (currentIndices.length === 0) return;

                // Update the appropriate index based on min/max
                if (preferenceType === 'min') {
                  const maxIndex = Math.max(...currentIndices);
                  const validNewIndex = Math.min(clampedIndex, maxIndex);
                  // Create new range from min to max
                  const newIndices: number[] = [];
                  for (let i = validNewIndex; i <= maxIndex; i++) {
                    newIndices.push(i);
                  }
                  currentIndices = newIndices;
                } else if (preferenceType === 'max') {
                  const minIndex = Math.min(...currentIndices);
                  const validNewIndex = Math.max(clampedIndex, minIndex);
                  // Create new range from min to max
                  const newIndices: number[] = [];
                  for (let i = minIndex; i <= validNewIndex; i++) {
                    newIndices.push(i);
                  }
                  currentIndices = newIndices;
                }

                // Build the updated preference data
                const updatedData: PreferenceDistributionData = {
                  stockExposure: preferenceField === 'stock_market_percent' ? currentIndices : (preferenceData.stockExposure || []),
                  currencyExposure: preferenceField === 'foreign_currency_exposure_percent' ? currentIndices : (preferenceData.currencyExposure || []),
                  foreignExposure: preferenceField === 'foreign_exposure_percent' ? currentIndices : (preferenceData.foreignExposure || []),
                };

                onPreferenceChange(updatedData);

                console.log('✓ Preference handle dragged:', {
                  field: preferenceField,
                  type: preferenceType,
                  newValue: newValue.toFixed(1),
                  newIndex: clampedIndex,
                  updatedIndices: currentIndices,
                });
              }
              return;
            }

            // Regular fund point drag handling
            if (pointData && onPathDrag) {
              const oldX = (pointData as any).originalX;
              const oldY = (pointData as any).originalY;
              const newX = value.x;
              const newY = value.y;

              const xChanged = Math.abs(oldX - newX) > 0.01;
              const yChanged = Math.abs(oldY - newY) > 0.01;

              if (xChanged || yChanged) {
                // שלח onPathDrag לכל הפוליסות שמרכיבות את העיגול
                const allPolicyInfos: Array<{ policy_number: string; product_type: number | null }> =
                  (pointData as any).all_policy_infos ?? [
                    { policy_number: (pointData as any).policy_number, product_type: (pointData as any).product_type }
                  ];

                allPolicyInfos.forEach(pInfo => {
                  onPathDrag({
                    policy_number: pInfo.policy_number,
                    product_type: pInfo.product_type,
                    path_code: (pointData as any).path_code,
                    old_x_value: oldX,
                    new_x_value: newX,
                    old_y_value: oldY,
                    new_y_value: newY,
                    x_field: xAxisField,
                    y_field: yAxisMode,
                  });
                });
              }
            }
          },
        },
        tooltip: {
          rtl: true,
          textDirection: 'rtl',
          titleAlign: 'right' as const,
          bodyAlign: 'right' as const,
          callbacks: {
            label: (context: any) => {
              const {
                label,
                fund_id,
                y,
                x,
                subspecialization,
                managing_corporation_legal_id,
                fund_classification,
                adjusted_product_type,
                client_name,
                amount,
                percentile_6m_yield,
                percentile_12m_yield,
                percentile_6m_bucket,
                percentile_12m_bucket,
                policy_number,
                isPreferenceHandle,
                preferenceType,
                preferenceField,
                stock_market_percent,
                foreign_exposure_percent,
                foreign_currency_exposure_percent,
              } = context.raw;

              // Special handling for preference handles
              if (isPreferenceHandle) {
                const typeLabel = preferenceType === 'min' ? 'מינימום' : 'מקסימום';
                const fieldLabel = preferenceField === 'stock_market_percent' ? 'חשיפה למניות' :
                                  preferenceField === 'foreign_currency_exposure_percent' ? 'חשיפה למט"ח' :
                                  'חשיפה לחו"ל';
                const value = context.raw.preferenceAxis === 'y' ? y : x;
                return [
                  `העדפה - ${fieldLabel}`,
                  `${typeLabel}: ${value.toFixed(1)}%`,
                ];
              }

              const lines = [
                `${label} (${fund_id})`,
              ];

              if (policy_number) {
                lines.push(`פוליסה: ${policy_number}`);
              }

              if (client_name) {
                lines.push(`לקוח: ${client_name}`);
              }

              // הצגת ערך ציר Y
              if (yAxisMode === 'yield') {
                lines.push(`תשואה: ${y.toFixed(2)}%`);
              } else if (yAxisMode === 'liquidity_year') {
                lines.push(`${getYAxisTitle()}: ${Math.round(y)}`);
              } else if (yAxisMode === 'standard_deviation') {
                lines.push(`${getYAxisTitle()}: ${y.toFixed(2)}%`);
              } else {
                lines.push(`${getYAxisTitle()}: ${y.toFixed(1)}%`);
              }

              // הצגת ערך ציר X
              if (xAxisField === 'liquidity_year') {
                lines.push(`${getXAxisTitle()}: ${Math.round(x)}`);
              } else {
                lines.push(`${getXAxisTitle()}: ${x.toFixed(1)}%`);
              }

              if (amount > 0) {
                lines.push(`סכום: ${formatAmount(amount)}`);
              }

              const formattedHandler = managing_corporation_legal_id
                ? formatHandlerCode(managing_corporation_legal_id)
                : "לא זמין";
              lines.push(`יצרן: ${formattedHandler}`);

              if (adjusted_product_type !== undefined && adjusted_product_type !== null) {
                lines.push(`סוג מוצר: ${formatAdjustedProductType(adjusted_product_type)}`);
              }

              if (colorMode === "subspecialization") {
                lines.push(`התמחות: ${subspecialization}`);
              } else if (colorMode === "fund_classification") {
                lines.push(`סיווג קרן: ${fund_classification || "-"}`);
              } else if (colorMode === "stock_exposure") {
                const exposureValue = stock_market_percent || 0;
                lines.push(`טווח חשיפה למניות: ${getStockExposureRange(exposureValue)}`);
              } else if (colorMode === "foreign_exposure") {
                const exposureValue = foreign_exposure_percent || 0;
                lines.push(`טווח חשיפה לחו"ל: ${getForeignExposureRange(exposureValue)}`);
              } else if (colorMode === "currency_exposure") {
                const exposureValue = foreign_currency_exposure_percent || 0;
                lines.push(`טווח חשיפה למט"ח: ${getCurrencyExposureRange(exposureValue)}`);
              } else if (colorMode === "percentile_6m") {
                lines.push(
                  `דירוג תשואה 6M: ${percentile_6m_yield.toFixed(1)}% (${percentileBucketLabels[percentile_6m_bucket]})`
                );
              } else if (colorMode === "percentile_12m") {
                lines.push(
                  `דירוג תשואה 12M: ${percentile_12m_yield.toFixed(1)}% (${percentileBucketLabels[percentile_12m_bucket]})`
                );
              }

              return lines;
            },
          },
          mode: "index" as const,
          intersect: true,
        },
        datalabels: {
          display: false,
        },
        legend: {
          display: false,
        },
        preferenceRanges: {
          ranges: preferenceRanges,
        } as any,
      },
      elements: {
        point: {
          pointStyle: (ctx: any) => ctx.raw?.isPreferenceHandle ? "rect" : "circle",
        },
      },
      onClick: (_event: any, elements: any[]) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const fund = chartData.datasets[0].data[index] as any;
          const id = fund.fund_id;

          // הדגש את הקרן
          setSelectedScatterFundId(prev => prev === id ? null : id);

          const newSelectedIds = actualSelectedFundIds.includes(id)
            ? actualSelectedFundIds.filter((x) => x !== id)
            : [...actualSelectedFundIds, id];

          // עדכון ללא אנימציה
          if (chartRef.current) {
            chartRef.current.options.animation = { duration: 0 };
          }

          actualOnSelectedFundsChange(newSelectedIds);

          // החזרת האנימציה לאחר העדכון
          setTimeout(() => {
            if (chartRef.current) {
              chartRef.current.options.animation = { duration: 750 };
            }
          }, 0);
        } else {
          // לחיצה על איזור ריק - בטל הדגשה
          setSelectedScatterFundId(null);
        }
      },
    };
  }, [chartData, actualYieldField, actualSelectedFundIds, colorMode, yAxisRange, selectedScatterFundId, xAxisField, yAxisMode, isDragEnabled]);


  return (
    <Box>
      {/* X-Axis and Y-Axis Field Selection in Title */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h6">
          {title}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>ציר X</InputLabel>
          <Select
            value={xAxisField}
            onChange={(e) => {
              actualSetXAxisField(e.target.value as typeof xAxisField);
              // איפוס הסינון של טווחי חשיפה כשמשנים ציר
              setSelectedExposureRanges([]);
            }}
            label="ציר X"
          >
            <MenuItem value="stock_market_percent" sx={{ direction: 'rtl' }}>% חשיפה למניות</MenuItem>
            <MenuItem value="foreign_currency_exposure_percent" sx={{ direction: 'rtl' }}>% חשיפה למט"ח</MenuItem>
            <MenuItem value="foreign_exposure_percent" sx={{ direction: 'rtl' }}>% חשיפה לחו"ל</MenuItem>
            <MenuItem value="liquidity_year" sx={{ direction: 'rtl' }}>שנת נזילות</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>ציר Y</InputLabel>
          <Select
            value={yAxisMode}
            onChange={(e) => {
              actualSetYAxisMode(e.target.value as typeof yAxisMode);
            }}
            label="ציר Y"
          >
            <MenuItem value="yield" sx={{ direction: 'rtl' }}>תשואה</MenuItem>
            <MenuItem value="stock_market_percent" sx={{ direction: 'rtl' }}>% חשיפה למניות</MenuItem>
            <MenuItem value="foreign_currency_exposure_percent" sx={{ direction: 'rtl' }}>% חשיפה למט"ח</MenuItem>
            <MenuItem value="foreign_exposure_percent" sx={{ direction: 'rtl' }}>% חשיפה לחו"ל</MenuItem>
            <MenuItem value="liquidity_year" sx={{ direction: 'rtl' }}>שנת נזילות</MenuItem>
            <MenuItem value="standard_deviation" sx={{ direction: 'rtl' }}>תנודתיות 5Y</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>צביעה</InputLabel>
          <Select
            value={colorMode}
            onChange={(e) => {
              actualSetColorMode(e.target.value as typeof colorMode);
              // איפוס כל הסינונים כשמשנים מצב צביעה
              if (e.target.value !== 'subspecialization') {
                setSelectedSubSpecializations([]);
              }
              if (e.target.value !== 'managing_corporation') {
                setSelectedManagingCorporations([]);
              }
              if (e.target.value !== 'stock_exposure' && e.target.value !== 'foreign_exposure' && e.target.value !== 'currency_exposure') {
                setSelectedExposureRanges([]);
              }
              if (e.target.value !== 'percentile_6m' && e.target.value !== 'percentile_12m') {
                setSelectedPercentileBuckets([]);
              }
            }}
            label="צביעה"
          >
            <MenuItem value="default" sx={{ direction: 'rtl' }}>לפי סוג מוצר</MenuItem>
            <MenuItem value="subspecialization" sx={{ direction: 'rtl' }}>לפי התמחות</MenuItem>
            <MenuItem value="managing_corporation" sx={{ direction: 'rtl' }}>לפי יצרן</MenuItem>
            <MenuItem value="fund_classification" sx={{ direction: 'rtl' }}>לפי סוג פנסיה</MenuItem>
            <MenuItem value="stock_exposure" sx={{ direction: 'rtl' }}>לפי חשיפה למניות</MenuItem>
            <MenuItem value="foreign_exposure" sx={{ direction: 'rtl' }}>לפי חשיפה לחו"ל</MenuItem>
            <MenuItem value="currency_exposure" sx={{ direction: 'rtl' }}>לפי חשיפה למט"ח</MenuItem>
            <MenuItem value="percentile_6m" sx={{ direction: 'rtl' }}>לפי דירוג 6M</MenuItem>
            <MenuItem value="percentile_12m" sx={{ direction: 'rtl' }}>לפי דירוג 12M</MenuItem>
          </Select>
        </FormControl>
        <MuiTooltip title={isDragEnabled ? "כבה מצב גרירה" : "הפעל מצב גרירה"}>
          <IconButton
            onClick={() => actualSetIsDragEnabled(!isDragEnabled)}
            color={isDragEnabled ? "primary" : "default"}
            sx={{
              border: isDragEnabled ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: isDragEnabled ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </MuiTooltip>
      </Box>

      {/* תצוגת סטטיסטיקות */}
      {/* {allHighlightedAmounts.length > 0 && (
        <Box mb={2} p={2} bgcolor="#f8f9fa" borderRadius={1}>
          <Typography variant="subtitle2" color="textSecondary" mb={1}>
            סטטיסטיקות סכומים:
          </Typography>
          <Box display="flex" gap={3}>
            <Typography variant="body2">
              סה"כ קרנות: {allHighlightedAmounts.length}
            </Typography>
            <Typography variant="body2">
              סכום כולל: {formatAmount(allHighlightedAmounts.reduce((sum, item) => sum + item.amount, 0))}
            </Typography>
          </Box>
        </Box>
      )} */}

      {/* Dropdowns for controls */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        {yAxisMode === 'yield' && (
          <ToggleButtonGroup
            value={actualYieldField}
            exclusive
            onChange={(_e, newVal) => newVal && actualOnYieldFieldChange(newVal)}
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1.5px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                margin: '0 2px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  borderColor: 'rgba(25, 118, 210, 0.5)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.16)',
                  }
                }
              }
            }}
          >
          <ToggleButton value="year_to_date_yield">YTD</ToggleButton>
           <ToggleButton value="cumulative_6m_yield">6M</ToggleButton>
          <ToggleButton value="cumulative_12m_yield">1Y</ToggleButton>

          <ToggleButton value="avg_annual_yield_trailing_3yrs">ממוצע 3 שנים</ToggleButton>
          <ToggleButton value="avg_annual_yield_trailing_5yrs">ממוצע 5 שנים</ToggleButton>
          <ToggleButton
            value="yield_trailing_3_yrs"
            sx={{ marginLeft: '16px !important' }}
          >
            מצטברת 3 שנים
          </ToggleButton>
          <ToggleButton value="yield_trailing_5_yrs">מצטברת 5 שנים</ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Exposure Range Multi-Select */}
        {(colorMode === 'stock_exposure' || colorMode === 'foreign_exposure' || colorMode === 'currency_exposure') && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>
              {colorMode === 'stock_exposure' ? 'טווחי חשיפה למניות' :
               colorMode === 'foreign_exposure' ? 'טווחי חשיפה לחו"ל' :
               'טווחי חשיפה למט"ח'}
            </InputLabel>
            <Select
              multiple
              value={selectedExposureRanges}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedExposureRanges(value);
              }}
              input={<OutlinedInput label={
                colorMode === 'stock_exposure' ? 'טווחי חשיפה למניות' :
                colorMode === 'foreign_exposure' ? 'טווחי חשיפה לחו"ל' :
                'טווחי חשיפה למט"ח'
              } />}
              renderValue={(selected) => selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`}
            >
              {getExposureRangesList(colorMode).map((range) => {
                const colors = getExposureColorsWithTransparency(colorMode);
                return (
                  <MenuItem key={range} value={range} sx={{ direction: 'rtl' }}>
                    <Checkbox checked={selectedExposureRanges.includes(range)} />
                    <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: colors[range],
                        }}
                      />
                      <ListItemText primary={`${range}%`} />
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {/* Sub-Specialization Multi-Select */}
        {colorMode === 'subspecialization' && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>התמחויות</InputLabel>
            <Select
              multiple
              value={selectedSubSpecializations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedSubSpecializations(value);
              }}
              input={<OutlinedInput label="התמחויות" />}
              renderValue={(selected) => 
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
            >
              {allSubSpecializations.map((spec) => (
                <MenuItem key={spec} value={spec} sx={{ direction: 'rtl' }}>
                  <Checkbox checked={selectedSubSpecializations.indexOf(spec) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: subspecializationColorMap[spec],
                      }}
                    />
                    <ListItemText primary={spec} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Managing Corporation Multi-Select */}
        {colorMode === 'managing_corporation' && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>יצרנים</InputLabel>
            <Select
              multiple
              value={selectedManagingCorporations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedManagingCorporations(value);
              }}
              input={<OutlinedInput label="יצרנים" />}
              renderValue={(selected) => 
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
            >
              {generalManagingCorporations.map((handler) => (
                <MenuItem key={handler.code} value={handler.code} sx={{ direction: 'rtl' }}>
                  <Checkbox checked={selectedManagingCorporations.indexOf(handler.code) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: addTransparency(colorHandlerCode(handler.code), 0.7),
                      }}
                    />
                    <ListItemText primary={handler.name} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Percentile Buckets Multi-Select */}
        {(colorMode === 'percentile_6m' || colorMode === 'percentile_12m') && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>דירוג תשואה {colorMode === 'percentile_6m' ? '6M' : '12M'}</InputLabel>
            <Select
              multiple
              value={selectedPercentileBuckets}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedPercentileBuckets(value);
              }}
              input={<OutlinedInput label={`דירוג תשואה ${colorMode === 'percentile_6m' ? '6M' : '12M'}`} />}
              renderValue={(selected) => selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`}
            >
              {allPercentileBuckets.map((bucket) => (
                <MenuItem key={bucket} value={bucket} sx={{ direction: 'rtl' }}>
                  <Checkbox checked={selectedPercentileBuckets.includes(bucket)} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: percentileBucketColors[bucket],
                      }}
                    />
                    <ListItemText primary={percentileBucketLabels[bucket]} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box height={400}>
        {chartData.datasets[0]?.data?.length > 0 ? (
          <Scatter ref={chartRef} data={chartData} options={chartOptions} />
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            bgcolor="#f5f5f5"
            borderRadius={1}
          >
            <Typography variant="h6" color="text.secondary">
              אין נתונים להצגה עבור הסינון הנוכחי
            </Typography>
          </Box>
        )}
      </Box>

      <FundAccordionList
        title="קרנות שנבחרו:"
        fundIds={actualSelectedFundIds}
        funds={fundData}
        borderColor="rgba(236, 72, 153, 1)"
        isVisible={true}
        onToggleVisibility={() => {}}
        selectedFundIds={actualSelectedFundIds}
        onRemoveFund={(id) => actualOnSelectedFundsChange(actualSelectedFundIds.filter((x) => x !== id))}
        fundAmountMap={fundAmountMap}
      />
    </Box>
  );
};

export default ScatterFundAmountChart;